"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
export interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  date?: string;
  [key: string]: unknown; // Allow additional custom fields
}
interface TimelineStyles {
  lineColor: string;
  activeLineColor: string;
  dotColor: string;
  activeDotColor: string;
  dotSize: string;
  titleColor: string;
  activeTitleColor: string;
  descriptionColor: string;
  dateColor: string;
}
const DOT_DURATION = 0.12;
const LINE_DURATION = 0.15;
const STAGGER = 0.08;
const LINE_OFFSET = 0.04;
function computeDelays(index: number, active: number | null, prev: number | null): {
  dotDelay: number;
  lineDelay: number;
} {
  const a = active ?? -1;
  const p = prev ?? -1;

  // Extending: top-to-bottom cascade from the previous frontier
  if (a > p) {
    if (p < 0) {
      return {
        dotDelay: index * STAGGER,
        lineDelay: index * STAGGER + LINE_OFFSET
      };
    }
    return {
      dotDelay: index > p ? (index - p) * STAGGER : 0,
      lineDelay: index === p ? 0 : index > p ? (index - p) * STAGGER + LINE_OFFSET : 0
    };
  }

  // Retracting: bottom-to-top reverse cascade
  if (a < p) {
    return {
      dotDelay: index <= p && index > a ? (p - index) * STAGGER : 0,
      lineDelay: index >= Math.max(a, 0) && index < p ? (p - 1 - index) * STAGGER + LINE_OFFSET : 0
    };
  }
  return {
    dotDelay: 0,
    lineDelay: 0
  };
}
interface TimelineItemProps {
  event: TimelineEvent;
  index: number;
  isDotActive: boolean;
  isLineActive: boolean;
  isLast: boolean;
  dotDelay: number;
  lineDelay: number;
  onEnter: () => void;
  onClick?: () => void;
  styles: TimelineStyles;
  customRender?: (event: TimelineEvent) => React.ReactNode;
  reverseDirection?: boolean;
}
const TimelineItem: React.FC<TimelineItemProps> = t0 => {
  const $ = _c(51);
  const {
    event,
    isDotActive,
    isLineActive,
    isLast,
    dotDelay,
    lineDelay,
    onEnter,
    onClick,
    styles,
    customRender,
    reverseDirection
  } = t0;
  const t1 = isDotActive ? styles.activeDotColor : styles.dotColor;
  const t2 = isDotActive ? styles.activeDotColor : "hsl(var(--background))";
  const t3 = isDotActive ? "scale(1.2)" : "scale(1)";
  const t4 = `${dotDelay}s`;
  let t5;
  if ($[0] !== styles.dotSize || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = {
      width: styles.dotSize,
      height: styles.dotSize,
      borderColor: t1,
      backgroundColor: t2,
      transform: t3,
      transitionProperty: "background-color, border-color, transform",
      transitionDuration: `${DOT_DURATION}s`,
      transitionDelay: t4,
      transitionTimingFunction: "ease"
    };
    $[0] = styles.dotSize;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const dotStyle = t5;
  const t6 = isLineActive ? "scaleY(1)" : "scaleY(0)";
  const t7 = reverseDirection ? "bottom" : "top";
  const t8 = `${lineDelay}s`;
  let t9;
  if ($[6] !== styles.activeLineColor || $[7] !== t6 || $[8] !== t7 || $[9] !== t8) {
    t9 = {
      backgroundColor: styles.activeLineColor,
      transform: t6,
      transformOrigin: t7,
      transitionProperty: "transform",
      transitionDuration: `${LINE_DURATION}s`,
      transitionDelay: t8,
      transitionTimingFunction: "ease-in-out"
    };
    $[6] = styles.activeLineColor;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const lineFillStyle = t9;
  const t10 = isDotActive ? styles.activeTitleColor : styles.titleColor;
  const t11 = `${dotDelay}s`;
  let t12;
  if ($[11] !== t10 || $[12] !== t11) {
    t12 = {
      color: t10,
      transitionProperty: "color",
      transitionDuration: `${DOT_DURATION}s`,
      transitionDelay: t11
    };
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const titleStyle = t12;
  const clickable = Boolean(onClick);
  let t13;
  if ($[14] !== clickable || $[15] !== onClick) {
    t13 = clickable ? e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    } : undefined;
    $[14] = clickable;
    $[15] = onClick;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = clickable ? "button" : undefined;
  const t15 = clickable ? 0 : undefined;
  const t16 = isLast ? "hidden" : "block";
  let t17;
  if ($[17] !== t16) {
    t17 = cn("absolute w-1", t16);
    $[17] = t16;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== styles.lineColor) {
    t18 = {
      backgroundColor: styles.lineColor,
      top: "20px",
      bottom: "-20px"
    };
    $[19] = styles.lineColor;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== lineFillStyle) {
    t19 = <div className="h-full w-full" style={lineFillStyle} />;
    $[21] = lineFillStyle;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t17 || $[24] !== t18 || $[25] !== t19) {
    t20 = <div className={t17} style={t18}>{t19}</div>;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== dotStyle) {
    t21 = <div className="relative z-10 rounded-full border-4" style={dotStyle} />;
    $[27] = dotStyle;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== t20 || $[30] !== t21) {
    t22 = <div className="hidden md:flex relative me-4 flex-col items-center pt-3">{t20}{t21}</div>;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const t23 = !isLast && "mb-6";
  let t24;
  if ($[32] !== t23) {
    t24 = cn("grow", t23);
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== customRender || $[35] !== event || $[36] !== styles.dateColor || $[37] !== styles.descriptionColor || $[38] !== titleStyle) {
    t25 = customRender ? customRender(event) : <><h3 className="text-lg font-semibold" style={titleStyle}>{event.title}</h3><p style={{
        color: styles.descriptionColor
      }}>{event.description}</p><span className="text-sm" style={{
        color: styles.dateColor
      }}>{event.date}</span></>;
    $[34] = customRender;
    $[35] = event;
    $[36] = styles.dateColor;
    $[37] = styles.descriptionColor;
    $[38] = titleStyle;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t24 || $[41] !== t25) {
    t26 = <div className={t24}>{t25}</div>;
    $[40] = t24;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== onClick || $[44] !== onEnter || $[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t22 || $[49] !== t26) {
    t27 = <div className="flex last:mb-0" onMouseEnter={onEnter} onFocus={onEnter} onTouchStart={onEnter} onClick={onClick} onKeyDown={t13} role={t14} tabIndex={t15}>{t22}{t26}</div>;
    $[43] = onClick;
    $[44] = onEnter;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t22;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  return t27;
};
interface AnimatedTimelineProps {
  events: TimelineEvent[];
  className?: string;
  styles?: Partial<TimelineStyles>;
  customEventRender?: (event: TimelineEvent) => React.ReactNode;
  onEventHover?: (event: TimelineEvent | null) => void;
  onEventClick?: (event: TimelineEvent) => void;
  initialActiveIndex?: number;
  reverseDirection?: boolean;
}
const defaultStyles: TimelineStyles = {
  lineColor: "#d1d5db",
  activeLineColor: "#22c55e",
  dotColor: "#d1d5db",
  activeDotColor: "#22c55e",
  dotSize: "1.5rem",
  titleColor: "inherit",
  activeTitleColor: "#22c55e",
  descriptionColor: "inherit",
  dateColor: "inherit"
};
export function AnimatedTimeline(t0) {
  const $ = _c(33);
  const {
    events,
    className: t1,
    styles: t2,
    customEventRender,
    onEventHover,
    onEventClick,
    initialActiveIndex,
    reverseDirection: t3
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const customStyles = t4;
  const reverseDirection = t3 === undefined ? false : t3;
  const t5 = initialActiveIndex ?? null;
  let t6;
  if ($[2] !== t5) {
    t6 = {
      active: t5,
      prev: null
    };
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const [state, setState] = useState(t6);
  let t7;
  if ($[4] !== customStyles) {
    t7 = {
      ...defaultStyles,
      ...customStyles
    };
    $[4] = customStyles;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const styles = t7;
  let t8;
  if ($[6] !== events || $[7] !== onEventHover) {
    t8 = index => {
      setState(s => s.active === index ? s : {
        active: index,
        prev: s.active
      });
      onEventHover?.(index !== null ? events[index] : null);
    };
    $[6] = events;
    $[7] = onEventHover;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const setActive = t8;
  let t9;
  if ($[9] !== className) {
    t9 = cn("relative", className);
    $[9] = className;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== setActive) {
    t10 = () => setActive(null);
    $[11] = setActive;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== customEventRender || $[14] !== events || $[15] !== onEventClick || $[16] !== reverseDirection || $[17] !== setActive || $[18] !== state || $[19] !== styles) {
    let t12;
    if ($[21] !== customEventRender || $[22] !== events.length || $[23] !== onEventClick || $[24] !== reverseDirection || $[25] !== setActive || $[26] !== state || $[27] !== styles) {
      t12 = (event, index_0) => {
        const isDotActive = state.active !== null && (reverseDirection ? index_0 >= state.active : index_0 <= state.active);
        const isLineActive = state.active !== null && (reverseDirection ? index_0 >= state.active : index_0 < state.active);
        let dotDelay;
        let lineDelay;
        if (reverseDirection) {
          const lastIndex = events.length - 1;
          const revIndex = lastIndex - index_0;
          const revLineIndex = lastIndex - index_0 - 1;
          const revActive = state.active !== null ? lastIndex - state.active : null;
          const revPrev = state.prev !== null ? lastIndex - state.prev : null;
          dotDelay = computeDelays(revIndex, revActive, revPrev).dotDelay;
          lineDelay = computeDelays(revLineIndex, revActive, revPrev).lineDelay;
        } else {
          const delays = computeDelays(index_0, state.active, state.prev);
          dotDelay = delays.dotDelay;
          lineDelay = delays.lineDelay;
        }
        return <TimelineItem key={event.id} event={event} index={index_0} isDotActive={isDotActive} isLineActive={isLineActive} isLast={index_0 === events.length - 1} dotDelay={dotDelay} lineDelay={lineDelay} onEnter={() => setActive(index_0)} onClick={onEventClick ? () => onEventClick(event) : undefined} styles={styles} customRender={customEventRender} reverseDirection={reverseDirection} />;
      };
      $[21] = customEventRender;
      $[22] = events.length;
      $[23] = onEventClick;
      $[24] = reverseDirection;
      $[25] = setActive;
      $[26] = state;
      $[27] = styles;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    t11 = events.map(t12);
    $[13] = customEventRender;
    $[14] = events;
    $[15] = onEventClick;
    $[16] = reverseDirection;
    $[17] = setActive;
    $[18] = state;
    $[19] = styles;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t9) {
    t12 = <div className={t9} onMouseLeave={t10}>{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
interface AnimatedTimelinePageProps {
  events?: TimelineEvent[];
  title?: string;
  containerClassName?: string;
  timelineStyles?: Partial<TimelineStyles>;
  customEventRender?: (events: TimelineEvent) => React.ReactNode;
  onEventHover?: (events: TimelineEvent | null) => void;
  onEventClick?: (events: TimelineEvent) => void;
  initialActiveIndex?: number;
}
export default function AnimatedTimelinePage(t0) {
  const $ = _c(16);
  const {
    events,
    title,
    containerClassName,
    timelineStyles,
    customEventRender,
    onEventHover,
    onEventClick,
    initialActiveIndex
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      id: "1",
      title: "Event 1",
      description: "Description 1",
      date: "2023-01-01"
    }, {
      id: "2",
      title: "Event 2",
      description: "Description 2",
      date: "2023-02-01"
    }, {
      id: "3",
      title: "Event 3",
      description: "Description 3",
      date: "2023-03-01"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const DefaultEvents = t1;
  let t2;
  if ($[1] !== containerClassName) {
    t2 = cn("container mx-auto rounded-lg bg-background px-8 pt-6 text-foreground", containerClassName);
    $[1] = containerClassName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = title || "Timeline";
  let t4;
  if ($[3] !== t3) {
    t4 = <h1 className="text-2xl font-bold">{t3}</h1>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = events || DefaultEvents;
  let t6;
  if ($[5] !== customEventRender || $[6] !== initialActiveIndex || $[7] !== onEventClick || $[8] !== onEventHover || $[9] !== t5 || $[10] !== timelineStyles) {
    t6 = <AnimatedTimeline events={t5} className="max-w-2xl" styles={timelineStyles} customEventRender={customEventRender} onEventHover={onEventHover} onEventClick={onEventClick} initialActiveIndex={initialActiveIndex} />;
    $[5] = customEventRender;
    $[6] = initialActiveIndex;
    $[7] = onEventClick;
    $[8] = onEventHover;
    $[9] = t5;
    $[10] = timelineStyles;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t4 || $[14] !== t6) {
    t7 = <div className={t2}>{t4}{t6}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}